/**
 * Shared headcount helpers for Nightwork / Rest-Day Work personnel.
 *
 * Role assignments (worker vs safety) are independent rows. The same person
 * can legitimately hold both. End users read "Assigned Personnel" as unique
 * people on site — not as a sum of role rows.
 */

import { formatDateShort } from './dateUtils';

export const assignmentStaffId = (assignment) => (
  assignment?.employee_staff_id || assignment?.employee || assignment?.staff_id || ''
);

const assignmentDisplayName = (assignment) => (
  assignment?.employee_name || assignmentStaffId(assignment)
);

export const summarizeAssignedPersonnel = (assignments = []) => {
  const active = assignments.filter((a) => a && a.is_active !== false);
  const workers = active.filter((a) => a.role === 'worker');
  const safety = active.filter((a) => a.role === 'safety');

  const uniqueIds = [...new Set(active.map(assignmentStaffId).filter(Boolean))];
  const workerIds = new Set(workers.map(assignmentStaffId).filter(Boolean));
  const safetyIds = new Set(safety.map(assignmentStaffId).filter(Boolean));
  const dualRoleIds = new Set([...workerIds].filter((id) => safetyIds.has(id)));

  const nameById = {};
  active.forEach((a) => {
    const id = assignmentStaffId(a);
    if (id && !nameById[id]) nameById[id] = assignmentDisplayName(a);
  });

  const dualRolePeople = [...dualRoleIds].map((staffId) => ({
    staffId,
    name: nameById[staffId] || staffId,
  }));

  return {
    workers,
    safety,
    uniqueCount: uniqueIds.length,
    dualRoleIds,
    dualRoleCount: dualRoleIds.size,
    dualRolePeople,
  };
};

export const uniquePersonnelListCount = ({
  personnelCount,
  workerCount = 0,
  safetyCount = 0,
} = {}) => {
  if (personnelCount != null) return personnelCount;
  return (workerCount || 0) + (safetyCount || 0);
};

/** Trade "Safety" on a Safety Personnel card restates the section heading. */
const SAFETY_TRADE_LABELS = new Set(['safety', 'safety department']);

export const isRedundantSafetyTrade = (tradeName, { inSafetySection = false } = {}) => {
  if (!inSafetySection || !tradeName) return false;
  return SAFETY_TRADE_LABELS.has(String(tradeName).trim().toLowerCase());
};

export const formatDualRoleNote = (people = []) => {
  const names = people.map((p) => p?.name).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) {
    return `${names[0]} is listed under Workers and Safety — counted once above.`;
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]} are listed under Workers and Safety — each counted once above.`;
  }
  const last = names[names.length - 1];
  return `${names.slice(0, -1).join(', ')} and ${last} are listed under Workers and Safety — each counted once above.`;
};

/**
 * Assignment window label. A single night is one date, not a range of itself.
 * Multi-day windows use the DS range separator (→).
 */
export const formatAssignmentDateLabel = (startIso, endIso) => {
  if (!startIso && !endIso) return '';
  if (!endIso || startIso === endIso) return formatDateShort(startIso || endIso);
  if (!startIso) return formatDateShort(endIso);
  return `${formatDateShort(startIso)} → ${formatDateShort(endIso)}`;
};

const assignmentWindow = (assignment, masterStart, masterEnd) => {
  const start = assignment.effective_from || masterStart;
  const end = assignment.effective_until || masterEnd;
  const stamped = !!(assignment.effective_from || assignment.effective_until);
  const matchesMaster = start === masterStart && end === masterEnd;
  return {
    start,
    end,
    isCustom: stamped && !matchesMaster,
  };
};

export const groupAssignmentsByDates = (assignments, request, scheduledDateCount = 0) => {
  const masterStart = request?.start_date;
  const masterEnd = request?.end_date;
  const dateCount = scheduledDateCount || request?.specific_dates?.length || 0;
  const groups = {};

  assignments.forEach((a) => {
    const { start, end, isCustom } = assignmentWindow(a, masterStart, masterEnd);
    const key = isCustom ? `${start}|${end}` : 'full';
    if (!groups[key]) {
      groups[key] = {
        label: isCustom
          ? formatAssignmentDateLabel(start, end)
          : request?.date_selection_type === 'range'
            ? formatAssignmentDateLabel(masterStart, masterEnd)
            : `${dateCount} date${dateCount !== 1 ? 's' : ''}`,
        isCustom,
        assignments: [],
      };
    }
    groups[key].assignments.push(a);
  });

  return Object.values(groups).sort((a, b) => Number(a.isCustom) - Number(b.isCustom));
};

/** Date-group chrome is only useful when people split across different windows. */
export const shouldShowAssignmentGroupMeta = (groups = []) => (
  groups.length > 1 || groups.some((g) => g.isCustom)
);
